/* tslint:disable:no-console */
import Axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { baseConfig } from '../baseConfig';
import country from './country';
import city from './city';
import webconfig from './webconfig';
import customer from './customer';
import cart from './cart';
import catalog, { internalCatalog } from './catalog';
import catalogReview from './catalogReview';
import Locale from '../utils/locale';
import ofd from './ofd';

// Enable reporting only in dev
// #TODO: Remove
const IS_DEV = process.env.NODE_ENV !== 'production';
const IS_E2E = !!process.env.E2E;

// Common headers for all calls
const commonHeaders = {
    ...baseConfig.commonHeaders,
    'Cache-Control': 'no-cache, max-age=0, must-revalidate, no-store', // Move this to baseConfig as well.
};

// --- --- --- --- --- --- AXIOS INSTANCES --- --- --- --- --- ---
// Axios instance (1):
// Usual calls from client
const api: AxiosInstance = Axios.create({
    // Requests are sent using a reverse proxy, check server.ts
    baseURL: '/',
    headers: {
        ...commonHeaders,
    },
});

// Axios instance (2):
// Internal fetch for webconfig (happens on SSR)
const apiInternal: AxiosInstance = Axios.create({
    baseURL: baseConfig.endpoints.webconfig,
    headers: {
        ...commonHeaders,
    },
});

const apiInternalCatalog: AxiosInstance = Axios.create({
    baseURL: baseConfig.endpoints.catalog,
    headers: {
        ...commonHeaders,
    },
});

// #TODO: Tidy this up @sarith
export const apiReviewsExternal: AxiosInstance = Axios.create({
    headers: {
        'Content-Type': 'application/json',
    },
});

// #TODO: Webconfig instance

// --- --- --- --- --- --- INTERCEPTORS --- --- --- --- --- ---
api.interceptors.request.use(
    config => {
        if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.info('✉️ ', config);
        }

        // ---  TEMPORARY HACK. #TODO: REMOVE. @NIPUN @GIAHN
        const spoofHeaders = {};
        if (IS_E2E) {
            spoofHeaders['user-agent'] = 'Googlebot';
        }

        return {
            ...config,
            headers: {
                ...config.headers,
                ...spoofHeaders,
                'X-Locale': Locale.getAPILocale(),
            },
        };
    },
    error => {
        if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.error('✉️ ', error);
        }

        return Promise.reject(error);
    },
);

api.interceptors.response.use(
    (response: AxiosResponse) => {
        if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.info('📩 ', response);
        }

        return response;
    },
    (error: AxiosError) => {
        const genericError = 'Something went wrong.';
        const offlineError = 'Looks like you’re offline. Make sure you’re connected to the internet and try again';

        /**
         * API sends an error message in the format
         * { error: error_message }
         */
        if (error.response && error.response.data) {
            /**
             * If the error is NOT initiated from the API (ie: for express/cloudflare errors)
             * then use generic error message for toast message and pass error info to sentry
             */
            if (typeof error.response.data === 'string') {
                const newData: {
                    error: string;
                    info: string;
                    status: number;
                } = {
                    error: genericError,
                    info: error.response.data,
                    status: error.response.status,
                };

                return Promise.reject(newData);
            }

            if (IS_DEV) {
                // eslint-disable-next-line no-console
                console.error('Error: ', error.response.data);
            }

            return Promise.reject(error.response.data);
        }
        if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.error('📩 ', error);
        }

        if (error.message === 'Network Error') {
            // #TODO
            // eslint-disable-next-line no-param-reassign
            error.message = offlineError;
        }

        return Promise.reject(error.message);
    },
);

// #TODO: Write interceptors for all Axios instances

// --- --- --- --- --- --- SERVICES --- --- --- --- --- ---
export const countrySVC = country(api);
export const citySVC = city(api);
export const webConfigSVC = webconfig(apiInternal);
export const customerSVC = customer(api);
export const cartSVC = cart(api);
export const catalogSVC = catalog(api);
export const internalCatalogSVC = internalCatalog(apiInternalCatalog);
export const review = catalogReview(apiReviewsExternal);
export const ofdSVC = ofd(api);
